import { addToast, Select, SelectItem } from '@heroui/react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { apiClient } from '../../../../api';
import { uploadToS3, uploadProfilePictureToS3 } from '../aws/s3FileUpload';
import moment from 'moment';
import VerificationDatePicker from '../../../components/common/ui/VerificationDatePicker';
import { getLocalTimeZone, today, CalendarDate } from '@internationalized/date';
import SecuritySettings from '../SecuritySettings/SecuritySettings';
import { useAuth } from '../../../../context/AuthContext';
import CustomButton from '../../../components/CustomButton';
import { FiEdit, FiUser, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import { FaBirthdayCake } from 'react-icons/fa';
import { addressFormatHelper } from '../../../../utils/common';
import CustomInput from '../../../components/CustomInput';
import PageHeader from '../../../components/common/ui/PageHeader';

const canadaProvincesAndTerritories = [
  { label: 'Alberta', key: 'Alberta' },
  { label: 'British Columbia', key: 'British Columbia' },
  { label: 'Manitoba', key: 'Manitoba' },
  { label: 'New Brunswick', key: 'New Brunswick' },
  { label: 'Nova Scotia', key: 'Nova Scotia' },
  { label: 'Newfoundland and Labrador', key: 'Newfoundland and Labrador' },
  { label: 'Prince Edward Island', key: 'Prince Edward Island' },
  { label: 'Ontario', key: 'Ontario' },
  { label: 'Quebec', key: 'Quebec' },
  { label: 'Saskatchewan', key: 'Saskatchewan' },
  { label: 'Northwest Territories', key: 'Northwest Territories' },
  { label: 'Yukon', key: 'Yukon' },
  { label: 'Nunavut', key: 'Nunavut' },
];

interface AddressData {
  addressLine1?: string;
  addressLine2?: string;
  country?: string;
  state?: string;
  city?: string;
  postalCode?: string;
}

interface UserData {
  firstName?: string;
  lastName?: string;
  name?: string;
  username?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  dateOfBirth?: string;
  bio?: string;
  address?: AddressData;
  currencyCode?: string;
  language?: string;
  profilePicture?: string;
}

const profileEditSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters')
    .matches(
      /^[a-zA-Z\s'-]+$/,
      'First name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters')
    .matches(
      /^[a-zA-Z\s'-]+$/,
      'Last name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  email: yup.string().email('Invalid email').required('Email is required'),
  mobile: yup.string().notRequired().nullable(),
  dateOfBirth: yup
    .string()
    .notRequired()
    .nullable()
    .test('is-18', 'You must be at least 18 years old', (value) => {
      if (!value) return true;
      const dob = new Date(value);
      if (isNaN(dob.getTime())) return false;
      const today = new Date();
      if (dob > today) return false;
      const eighteenthBirthday = new Date(dob);
      eighteenthBirthday.setFullYear(dob.getFullYear() + 18);
      return eighteenthBirthday <= today;
    }),
  address: yup.object().shape({
    addressLine1: yup.string().notRequired().nullable(),
    addressLine2: yup.string().notRequired().nullable(),
    country: yup.string().notRequired().nullable(),
    state: yup.string().notRequired().nullable(),
    city: yup.string().notRequired().nullable(),
    postalCode: yup.string().notRequired().nullable(),
  }),
});

const AccountSettings = () => {
  const { user, userId, isAuthenticated, isVerifyUser } = useAuth();
  const [userData, setUserData] = useState<UserData>({});
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editSaveLoading, setEditSaveLoading] = useState(false);
  const [profilePicPreview, setProfilePicPreview] = useState<string | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFormFileInputRef = useRef<HTMLInputElement>(null);
  const isEditOpenRef = useRef(false);
  const pendingFileRef = useRef<File | null>(null);
  const [pictureRemoved, setPictureRemoved] = useState(false);
  const saveAbortRef = useRef<AbortController | null>(null);

  const form = useForm({
    resolver: yupResolver(profileEditSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      dateOfBirth: '',
      address: {
        addressLine1: '',
        addressLine2: '',
        country: '',
        state: '',
        city: '',
        postalCode: '',
      },
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  const InfoRow = ({
    icon,
    label,
    value,
  }: {
    icon?: React.ReactNode;
    label: string;
    value: string;
  }) => (
    <div className="flex flex-col gap-1 py-3">
      <div className="flex items-center gap-2">
        <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] bg-[var(--secondary)]/10 text-[#317690]">
          {icon}
        </span>
        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p className="text-[13px] font-semibold text-gray-700 ml-[42px] break-words">
        {value || '—'}
      </p>
    </div>
  );

  const fetchUserData = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      addToast({
        title: 'User ID not found. Please log in again.',
        description: 'User ID not found. Please log in again.',
        radius: 'md',
        color: 'danger',
      });
      return;
    }

    try {
      setLoading(true);

      if (!isAuthenticated) {
        addToast({
          title:
            'You are not logged in. Please log in to view your account settings.',
          description:
            'You are not logged in. Please log in to view your account settings.',
          radius: 'md',
          color: 'danger',
        });
        setLoading(false);
        return;
      }

      const response = await apiClient.get(`/user/api/v1/user/${userId}`);

      if (response.status === 200 && response.data) {
        const apiData = response.data.user || response.data;

        let normalizedAddress: UserData['address'] = {
          addressLine1: '',
          addressLine2: '',
          country: '',
          state: '',
          city: '',
          postalCode: '',
        };

        const rawAddress = apiData.address;
        if (Array.isArray(rawAddress)) {
          const preferred =
            rawAddress.find((a: any) => a?.isLegalAddress) || rawAddress[0];
          if (preferred) {
            normalizedAddress = {
              addressLine1:
                preferred.addressLine1 ||
                preferred.street ||
                preferred.line1 ||
                '',
              addressLine2: preferred.addressLine2 || preferred.line2 || '',
              country: preferred.country || '',
              state:
                preferred.state || preferred.province || preferred.region || '',
              city: preferred.city || '',
              postalCode:
                preferred.postalCode ||
                preferred.zipCode ||
                preferred.zip ||
                '',
            };
          }
        } else if (rawAddress && typeof rawAddress === 'object') {
          normalizedAddress = {
            addressLine1:
              rawAddress.addressLine1 ||
              rawAddress.street ||
              rawAddress.line1 ||
              '',
            addressLine2: rawAddress.addressLine2 || rawAddress.line2 || '',
            country: rawAddress.country || '',
            state:
              rawAddress.state ||
              rawAddress.province ||
              rawAddress.region ||
              '',
            city: rawAddress.city || '',
            postalCode:
              rawAddress.postalCode ||
              rawAddress.zipCode ||
              rawAddress.zip ||
              '',
          };
        }
        const adaptedData: UserData = {
          firstName: apiData.firstName || '',
          lastName: apiData.lastName || '',
          name: apiData.name || apiData.fullName || apiData.displayName || '',
          email: apiData.email || '',
          mobile: apiData.mobile || apiData.phone || apiData.phoneNumber || '',
          dateOfBirth: apiData.dateOfBirth || apiData.dob || '',
          bio: apiData.bio || apiData.description || '',
          language: apiData.language || apiData.preferredLanguage || '',
          currencyCode: apiData.currencyCode || apiData.currency || '',
          profilePicture:
            apiData.profilePicture ||
            apiData.profileImage ||
            apiData.avatar ||
            apiData.picture ||
            '',
          address: normalizedAddress,
        };

        if (
          !adaptedData.firstName &&
          !adaptedData.lastName &&
          !adaptedData.email
        ) {
          adaptedData.firstName = user?.firstName || 'N/A';
          adaptedData.lastName = user?.lastName || '';
          adaptedData.email = user?.email || 'N/A';
          adaptedData.mobile = '+1234567890';
          adaptedData.bio = 'This is a sample bio for testing purposes.';
          adaptedData.currencyCode = 'USD';
          adaptedData.language = 'English';
        }

        setUserData(adaptedData);
      } else {
        addToast({
          title: 'Failed to Fetch User Data',
          description: 'Failed to fetch user data. Please try again later.',
          radius: 'md',
          color: 'danger',
        });
      }
    } catch (err: unknown) {
      console.error('Error fetching user data:', err);
      addToast({
        title: 'Error Fetching User Data',
        description: 'An error occurred while fetching your account data.',
        radius: 'md',
        color: 'danger',
      });
    } finally {
      setLoading(false);
    }
  }, [userId, isAuthenticated, user?.firstName, user?.lastName, user?.email]);

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId, fetchUserData]);

  const handleOpenEditForm = () => {
    reset({
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      mobile: userData.mobile || userData.phone || '',
      dateOfBirth: userData.dateOfBirth || '',
      address: {
        addressLine1: userData.address?.addressLine1 || '',
        addressLine2: userData.address?.addressLine2 || '',
        country: userData.address?.country || '',
        state: userData.address?.state || '',
        city: userData.address?.city || '',
        postalCode: userData.address?.postalCode || '',
      },
    });
    setProfilePicPreview(null);
    pendingFileRef.current = null;
    setPictureRemoved(false);
    if (editFormFileInputRef.current) editFormFileInputRef.current.value = '';
    setShowEditForm(true);
    isEditOpenRef.current = true;
  };

  const handleCloseEditForm = () => {
    if (saveAbortRef.current) {
      saveAbortRef.current.abort();
      saveAbortRef.current = null;
    }
    if (profilePicPreview) {
      URL.revokeObjectURL(profilePicPreview);
    }
    setShowEditForm(false);
    setEditSaveLoading(false);
    setProfilePicPreview(null);
    pendingFileRef.current = null;
    setPictureRemoved(false);
    if (editFormFileInputRef.current) editFormFileInputRef.current.value = '';
    isEditOpenRef.current = false;
  };

  const saveUserData = async (dataToSave: UserData, signal?: AbortSignal) => {
    if (!userId) {
      throw new Error('User ID not found. Please log in again.');
    }

    if (!isAuthenticated) {
      throw new Error(
        'You are not logged in. Please log in to save your account settings.'
      );
    }

    try {
      if (!dataToSave.firstName || !dataToSave.lastName || !dataToSave.email) {
        throw new Error('First name, last name, and email are required fields');
      }

      const formattedData = {
        ...dataToSave,
        dateOfBirth:
          dataToSave.dateOfBirth && moment(dataToSave.dateOfBirth).isValid()
            ? moment(dataToSave.dateOfBirth).format('YYYY-MM-DD')
            : dataToSave.dateOfBirth || null,
        profilePicture: dataToSave.profilePicture
          ? dataToSave.profilePicture
          : null,
        firstName: dataToSave.firstName || '',
        lastName: dataToSave.lastName || '',
        name: `${dataToSave.firstName || ''} ${dataToSave.lastName || ''}`.trim(),
        email: dataToSave.email || '',
        mobile: dataToSave.mobile || '',
        phone: dataToSave.phone || '',
        bio: dataToSave.bio || '',
        language: dataToSave.language || '',
        currencyCode: dataToSave.currencyCode || '',
        address: dataToSave.address
          ? {
              addressLine1: dataToSave.address.addressLine1 || '',
              addressLine2: dataToSave.address.addressLine2 || '',
              country: dataToSave.address.country || '',
              state: dataToSave.address.state || '',
              city: dataToSave.address.city || '',
              postalCode: dataToSave.address.postalCode || '',
            }
          : {},
      };

      const response = await apiClient.put(
        `/user/api/v1/user/${userId}`,
        formattedData,
        {
          timeout: 45000,
          signal,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(
          `Failed to update user data. Server returned status ${response.status}`
        );
      }

      if (!response.data) {
        throw new Error(
          'Failed to update user data. No data returned from server.'
        );
      }

      const data = response.data as unknown;
      const normalized: UserData =
        typeof data === 'object' &&
        data !== null &&
        'user' in (data as Record<string, unknown>)
          ? (data as { user: UserData }).user
          : (data as UserData);
      return normalized;
    } catch (error: unknown) {
      console.error('Error in saveUserData:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to save user data: ${error.message}`);
      } else {
        throw new Error('Failed to save user data due to an unknown error');
      }
    }
  };

  const onSave = async (data: any) => {
    saveAbortRef.current = new AbortController();
    const signal = saveAbortRef.current.signal;

    try {
      setEditSaveLoading(true);

      let pictureUrl = userData.profilePicture || '';
      let shouldRemovePicture = pictureRemoved;
      if (pendingFileRef.current) {
        shouldRemovePicture = false;
        try {
          if (!userId) throw new Error('User ID not found');
          const uploadResult = await uploadProfilePictureToS3(
            pendingFileRef.current,
            userId
          );
          if (uploadResult?.fullUrl) pictureUrl = uploadResult.fullUrl;
        } catch {
          addToast({
            title: 'Upload failed',
            description: 'Could not upload profile picture.',
            color: 'danger',
          });
          return;
        }
      }

      if (signal.aborted) return;

      const savePayload: UserData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        mobile: data.mobile,
        dateOfBirth: data.dateOfBirth,
        address: data.address,
        profilePicture: shouldRemovePicture ? '' : pictureUrl || undefined,
      };
      await saveUserData(savePayload, signal);
      if (signal.aborted) return;
      await fetchUserData();
      if (signal.aborted) return;
      addToast({
        title: 'Account Information Updated Successfully',
        description: 'Your account information has been updated successfully!',
        radius: 'md',
        color: 'success',
      });
      handleCloseEditForm();
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      if (
        err &&
        typeof err === 'object' &&
        'code' in err &&
        (err as any).code === 'ERR_CANCELED'
      )
        return;
      console.error('Error updating user data:', err);
      addToast({
        title: 'Error Updating User Data',
        description:
          err instanceof Error
            ? err.message
            : 'An error occurred while updating your account data.',
        radius: 'md',
        color: 'danger',
      });
    } finally {
      setEditSaveLoading(false);
      saveAbortRef.current = null;
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      addToast({
        title: 'Invalid File Type',
        description: 'Invalid file type. Please upload a .jpg or .png file.',
        radius: 'md',
        color: 'danger',
      });
      return;
    }

    if (file.size > 512000) {
      addToast({
        title: 'File is too Large',
        description: 'File is too large. Maximum size is 500KB.',
        radius: 'md',
        color: 'danger',
      });
      return;
    }

    try {
      const imageUrl = await uploadToS3(file, 'profile-images');

      if (!imageUrl) {
        throw new Error('Failed to get image URL after upload');
      }

      const updatedUserData = {
        ...userData,
        profilePicture: imageUrl,
      };

      setUserData(updatedUserData);
      await saveUserData(updatedUserData);
      await fetchUserData();
      addToast({
        title: 'Profile Picture Uploaded and Saved Successfully',
        description: 'Profile picture uploaded and saved successfully!',
        radius: 'md',
        color: 'success',
      });

      window.dispatchEvent(
        new CustomEvent('profilePictureUpdated', {
          detail: {
            profileImage: imageUrl,
            userId: userId,
          },
        })
      );
    } catch (err: unknown) {
      console.error('Error uploading profile picture:', err);
      addToast({
        title: 'Failed to Upload Profile Picture',
        description: 'Failed to upload profile picture. Please try again.',
        radius: 'md',
        color: 'danger',
      });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleProfilePicSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      addToast({
        title: 'Invalid File Type',
        description:
          'Invalid file type. Please upload a .jpg, .png, or .webp file.',
        radius: 'md',
        color: 'danger',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      addToast({
        title: 'File is too Large',
        description: 'File is too large. Maximum size is 5MB.',
        radius: 'md',
        color: 'danger',
      });
      return;
    }

    if (profilePicPreview) URL.revokeObjectURL(profilePicPreview);
    setProfilePicPreview(URL.createObjectURL(file));
    pendingFileRef.current = file;
    setPictureRemoved(false);
  };

  const handleProfilePicRemove = () => {
    if (profilePicPreview) {
      URL.revokeObjectURL(profilePicPreview);
      setProfilePicPreview(null);
    }
    pendingFileRef.current = null;
    setPictureRemoved(true);
    if (editFormFileInputRef.current) editFormFileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center">
        <div className="text-center">Loading account data...</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Profile Settings"
        description="Manage your personal information"
      />

      <div className="w-full max-w-5xl">
        <div className="flex flex-col lg:flex-row gap-5">
          <div className="w-full lg:w-[35%]">
            <div className="bg-white border border-[#dfdeded5] rounded-2xl shadow-sm overflow-hidden">
              <div className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-4">
                    {userData.profilePicture ? (
                      <img
                        src={userData.profilePicture}
                        alt="Profile"
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                        style={{
                          boxShadow: '0 0 0 3px white, 0 0 0 5px #0095b7',
                        }}
                      />
                    ) : (
                      <div
                        className="w-16 h-16 rounded-full bg-[#207d99] flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
                        style={{
                          boxShadow: '0 0 0 3px white, 0 0 0 5px #0095b7',
                        }}
                      >
                        <FiUser className="w-6 h-6" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-base font-semibold text-gray-800 flex items-center gap-1.5 max-w-[180px]">
                        <span className="truncate">
                          {`${userData.firstName ?? ''} ${userData.lastName ?? ''}`.trim() ||
                            'User Name'}
                        </span>
                        {isVerifyUser && (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="#22C55E"
                            className="flex-shrink-0"
                          >
                            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                          </svg>
                        )}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {userData.email || user?.email || '—'}
                      </p>
                      {user?.createdAt && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Member since{' '}
                          {moment(user.createdAt).isValid()
                            ? moment(user.createdAt).format('MMMM YYYY')
                            : '—'}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleOpenEditForm}
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--secondary)]/10 hover:bg-[var(--secondary)]/20 text-[#317690] transition-colors"
                    aria-label="Edit profile"
                    title="Edit profile"
                  >
                    <FiEdit size={14} />
                  </button>
                </div>

                <div className="border-t border-[#dfdeded5] pt-4">
                  <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Contact Details
                  </h3>
                  <div className="divide-y divide-gray-100">
                    <InfoRow
                      icon={<FiMail size={15} />}
                      label="Email"
                      value={userData.email || user?.email || '—'}
                    />
                    <InfoRow
                      icon={<FiPhone size={15} />}
                      label="Phone"
                      value={
                        userData.mobile ||
                        userData.phone ||
                        user?.phoneNumber ||
                        '—'
                      }
                    />
                    <InfoRow
                      icon={<FaBirthdayCake size={15} />}
                      label="Date of Birth"
                      value={
                        userData.dateOfBirth
                          ? moment(userData.dateOfBirth).isValid()
                            ? moment(userData.dateOfBirth).format('LL')
                            : String(userData.dateOfBirth)
                          : '—'
                      }
                    />
                    <InfoRow
                      icon={<FiMapPin size={15} />}
                      label="Address"
                      value={
                        addressFormatHelper(userData.address as any) || '—'
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[50%]">
            <SecuritySettings />
          </div>
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".jpg,.jpeg,.png"
        onChange={handleFileUpload}
      />
      <input
        type="file"
        ref={editFormFileInputRef}
        className="hidden"
        accept=".jpg,.jpeg,.png,.webp"
        onChange={handleProfilePicSelect}
      />

      <Modal
        key={showEditForm ? 'edit-open' : 'edit-closed'}
        isOpen={showEditForm}
        onClose={handleCloseEditForm}
        size="2xl"
        scrollBehavior="inside"
        isDismissable={!editSaveLoading}
        isKeyboardDismissDisabled={editSaveLoading}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <h2 className="text-title">Edit Profile</h2>
            <p className="text-xs font-normal text-gray-500">
              Update your personal information and preferences
            </p>
          </ModalHeader>
          <ModalBody className="px-6">
            <div className="mb-6">
              <div className="flex items-center gap-3">
                {!pictureRemoved &&
                (profilePicPreview || userData.profilePicture) ? (
                  <img
                    src={profilePicPreview || userData.profilePicture}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div
                    className="w-20 h-20 rounded-full border-2 border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400"
                    aria-hidden
                  >
                    <FiUser className="w-10 h-10" />
                  </div>
                )}
                <div className="flex gap-2">
                  <CustomButton
                    color="primary"
                    variant="flat"
                    size="sm"
                    onPress={() => editFormFileInputRef.current?.click()}
                    isDisabled={editSaveLoading}
                  >
                    Upload New
                  </CustomButton>
                  {!pictureRemoved &&
                    (profilePicPreview || userData.profilePicture) && (
                      <CustomButton
                        color="danger"
                        variant="flat"
                        size="sm"
                        onPress={handleProfilePicRemove}
                        isDisabled={editSaveLoading}
                      >
                        Remove
                      </CustomButton>
                    )}
                </div>
              </div>
              <p className="text-caption text-gray-500 mt-2">
                Supported formats: JPG, PNG, WebP. Maximum size: 5MB.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-title-bold mb-4">General Information</h3>
                  <div className="flex flex-col gap-4">
                    <Controller
                      control={control}
                      name="firstName"
                      render={({ field }) => (
                        <CustomInput
                          type="text"
                          label="First Name"
                          isRequired
                          maxLength={50}
                          placeholder="Enter your first name"
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          size="md"
                          variant="bordered"
                          labelPlacement="outside"
                          isInvalid={!!errors.firstName}
                          errorMessage={errors.firstName?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="lastName"
                      render={({ field }) => (
                        <CustomInput
                          type="text"
                          label="Last Name"
                          isRequired
                          maxLength={50}
                          placeholder="Enter your last name"
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          size="md"
                          variant="bordered"
                          labelPlacement="outside"
                          isInvalid={!!errors.lastName}
                          errorMessage={errors.lastName?.message}
                        />
                      )}
                    />
                    <CustomInput
                      label="Email Address"
                      type="email"
                      isRequired
                      placeholder="Enter your email"
                      value={userData.email || ''}
                      size="md"
                      variant="bordered"
                      labelPlacement="outside"
                      isDisabled
                      description="Email cannot be edited"
                    />
                    <Controller
                      control={control}
                      name="mobile"
                      render={({ field }) => (
                        <CustomInput
                          type="tel"
                          label="Phone Number"
                          placeholder="+1 xxx xxxx xxx"
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          size="md"
                          variant="bordered"
                          labelPlacement="outside"
                          isInvalid={!!errors.mobile}
                          errorMessage={errors.mobile?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="dateOfBirth"
                      render={({ field }) => (
                        <VerificationDatePicker
                          label="Date of Birth"
                          description="You must be at least 18 years old"
                          variant="bordered"
                          labelPlacement="outside"
                          hideTimeZone
                          defaultValue={
                            (field.value && moment(field.value).isValid()
                              ? new CalendarDate(
                                  moment(field.value).year(),
                                  moment(field.value).month() + 1,
                                  moment(field.value).date()
                                )
                              : undefined) as any
                          }
                          yearRange={{
                            min: 1900,
                            max: today(getLocalTimeZone()).year,
                          }}
                          calendarProps={{
                            isDateUnavailable: (date) =>
                              date.compare(today(getLocalTimeZone())) > 0,
                          }}
                          onChange={(val) => {
                            if (!val) return;
                            const d = val as CalendarDate;
                            field.onChange(
                              `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`
                            );
                          }}
                          isInvalid={!!errors.dateOfBirth}
                          errorMessage={errors.dateOfBirth?.message}
                          classNames={{
                            inputWrapper: 'border border-gray-300 text-xs',
                            description: 'text-[11px]',
                            errorMessage: 'text-[11px] text-red-500 font-thin',
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-title-bold mb-4">Address Information</h3>
                  <div className="flex flex-col gap-4">
                    <Controller
                      control={control}
                      name="address.addressLine1"
                      render={({ field }) => (
                        <CustomInput
                          type="text"
                          label="Address Line 1"
                          placeholder="Enter address line 1"
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          size="md"
                          variant="bordered"
                          labelPlacement="outside"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="address.addressLine2"
                      render={({ field }) => (
                        <CustomInput
                          type="text"
                          label="Address Line 2"
                          placeholder="Enter address line 2 (optional)"
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          size="md"
                          variant="bordered"
                          labelPlacement="outside"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="address.country"
                      render={({ field }) => (
                        <CustomInput
                          type="text"
                          label="Country"
                          placeholder="Enter your country"
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          size="md"
                          variant="bordered"
                          labelPlacement="outside"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="address.state"
                      render={({ field }) => (
                        <div className="flex flex-col gap-1">
                          <label className="text-xs text-gray-600 font-medium">
                            State/Province
                          </label>
                          <Select
                            placeholder="Select your state"
                            selectedKeys={field.value ? [field.value] : []}
                            onSelectionChange={(keys) => {
                              const selectedKey = Array.from(keys)[0] as string;
                              field.onChange(selectedKey || '');
                            }}
                            size="md"
                            variant="bordered"
                            classNames={{
                              trigger: 'border border-gray-300 text-xs',
                              value: 'text-xs',
                              listbox: 'text-xs',
                            }}
                          >
                            {canadaProvincesAndTerritories.map((s) => (
                              <SelectItem key={s.key}>{s.label}</SelectItem>
                            ))}
                          </Select>
                        </div>
                      )}
                    />
                    <Controller
                      control={control}
                      name="address.city"
                      render={({ field }) => (
                        <CustomInput
                          type="text"
                          label="City"
                          maxLength={50}
                          placeholder="Enter your city"
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          size="md"
                          variant="bordered"
                          labelPlacement="outside"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="address.postalCode"
                      render={({ field }) => (
                        <CustomInput
                          type="text"
                          label="Postal Code"
                          placeholder="Enter postal code"
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          size="md"
                          variant="bordered"
                          labelPlacement="outside"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="flex justify-end gap-2 px-6 py-4">
            <CustomButton
              color="danger"
              variant="light"
              label="Cancel"
              onPress={handleCloseEditForm}
              isDisabled={editSaveLoading}
            />
            <CustomButton
              color="primary"
              label={editSaveLoading ? 'Saving...' : 'Save Changes'}
              onPress={handleSubmit(onSave)}
              isLoading={editSaveLoading}
              isDisabled={editSaveLoading}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AccountSettings;
